"use client";

import React, { useState } from "react";
import { 
  X, 
  Copy, 
  Check, 
  CreditCard, 
  Building2, 
  QrCode, 
  Globe, 
  Send, 
  DollarSign, 
  ShieldCheck, 
  Sparkles,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/Button";

interface PaymentManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultClientName?: string;
  defaultAmount?: number;
  defaultCurrency?: "INR" | "USD";
}

export default function PaymentManagementModal({
  isOpen,
  onClose,
  defaultClientName = "",
  defaultAmount = 32500,
  defaultCurrency = "INR",
}: PaymentManagementModalProps) {
  const [activeTab, setActiveTab] = useState<"india" | "international" | "quickInvoice">(
    defaultCurrency === "USD" ? "international" : "india"
  );

  // Copied state trackers
  const [copiedAccount, setCopiedAccount] = useState(false);
  const [copiedUpi, setCopiedUpi] = useState(false);
  const [copiedPaypal, setCopiedPaypal] = useState(false);
  const [copiedFullMessage, setCopiedFullMessage] = useState(false);

  // Quick Invoice State
  const [clientName, setClientName] = useState(defaultClientName || "Acme Realty");
  const [clientPhone, setClientPhone] = useState("+91 98808 93416");
  const [invoiceCurrency, setInvoiceCurrency] = useState<"INR" | "USD">(defaultCurrency);
  const [invoiceAmount, setInvoiceAmount] = useState<number>(
    defaultCurrency === "USD" ? (defaultAmount < 5000 ? defaultAmount : 625) : defaultAmount
  );
  const [invoicePurpose, setInvoicePurpose] = useState<string>("Tier 2: 50% Kickoff Setup Deposit");

  if (!isOpen) return null;

  // Indian Bank Details
  const bankDetails = {
    bankName: "HDFC Bank Ltd",
    accountNumber: "50100455367410",
    accountType: "Bank Account",
    ifscCode: "HDFC0004895",
    branch: "Bangalore, India",
    beneficiaryName: "Johnson D",
    upiId: "johnsondev02@ybl",
    supportPhone: "+91 98808 93416",
  };

  // International PayPal & Wire Details
  const internationalDetails = {
    paypalHandle: "JOHNSOND216",
    paypalLink: "https://paypal.me/JOHNSOND216",
    bankName: "HDFC Bank Ltd",
    accountNumber: "50100455367410",
    swiftCode: "HDFCINBB",
    beneficiaryName: "Johnson D",
    acceptedCurrencies: "USD, EUR, GBP, AUD, CAD, SGD",
  };

  // Dynamic UPI payment URI for QR code
  const upiUri = `upi://pay?pa=${bankDetails.upiId}&pn=Johnson%20D&cu=INR&tn=${encodeURIComponent(invoicePurpose)}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(upiUri)}`;

  // Copy Helpers
  const copyToClipboard = (text: string, setCopied: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Formatted Message Generator
  const generateFormattedMessage = () => {
    // When in 1-Click dispatch tab, strictly format by invoiceCurrency!
    const isDomestic = activeTab === "india" || (activeTab === "quickInvoice" && invoiceCurrency === "INR");

    if (isDomestic) {
      return (
        `*JOHNSON SOFTWARES - OFFICIAL PAYMENT DETAILS*\n\n` +
        `Dear *${clientName}*,\n` +
        `Please remit the fee for *${invoicePurpose}* (₹${invoiceAmount.toLocaleString("en-IN")} INR) using the verified details below:\n\n` +
        `⚠️ *TERMS: STRICTLY 100% NON-REFUNDABLE • 3-MONTH MINIMUM TENURE*\n` +
        `_All setup deposits and retainers are strictly non-refundable once engineering capacity is reserved. A minimum 3-month billing tenure is mandatory to prevent single-tenant code abuse and guarantee 99.95% high availability._\n\n` +
        `🏦 *HDFC BANK ACCOUNT DETAILS:*\n` +
        `• *Beneficiary*: ${bankDetails.beneficiaryName}\n` +
        `• *Bank Name*: ${bankDetails.bankName}\n` +
        `• *Account Type*: ${bankDetails.accountType}\n` +
        `• *Account Number*: ${bankDetails.accountNumber}\n` +
        `• *IFSC Code*: ${bankDetails.ifscCode}\n\n` +
        `📱 *INSTANT UPI PAYMENT:*\n` +
        `• *UPI ID*: ${bankDetails.upiId}\n` +
        `• *Supported Apps*: Google Pay, PhonePe, Paytm, BHIM, Any UPI App\n\n` +
        `_Kindly reply with the transaction UTR / payment screenshot once remitted._`
      );
    } else {
      return (
        `*JOHNSON SOFTWARES - INTERNATIONAL PAYMENT DETAILS*\n\n` +
        `Dear *${clientName}*,\n` +
        `Here are the payment details for *${invoicePurpose}* ($${invoiceAmount.toLocaleString("en-US")} USD):\n\n` +
        `⚠️ *TERMS: STRICTLY 100% NON-REFUNDABLE • 3-MONTH MINIMUM TENURE*\n` +
        `_All setup deposits and retainers are strictly non-refundable once engineering capacity is reserved. A minimum 3-month billing tenure is mandatory to prevent system misuse._\n\n` +
        `💳 *PRIMARY OPTION: PAYPAL / CREDIT CARD:*\n` +
        `• *Direct Checkout Link*: ${internationalDetails.paypalLink}\n` +
        `• *Supported*: Visa, Mastercard, Amex, Apple Pay, PayPal Balance\n\n` +
        `🏦 *INTERNATIONAL WIRE TRANSFER (SWIFT):*\n` +
        `• *Bank*: ${internationalDetails.bankName}\n` +
        `• *SWIFT Code*: ${internationalDetails.swiftCode}\n` +
        `• *Account Number*: ${internationalDetails.accountNumber}\n` +
        `• *Beneficiary*: ${internationalDetails.beneficiaryName}\n\n` +
        `_Once remitted, your dedicated cloud instance and onboarding call will be scheduled within 24 hours._`
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md shadow-emerald-500/20">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                Payment &amp; Remittance Management
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800">
                  Verified Active
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Official Bank Remittance, UPI &amp; International PayPal Credentials
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Channel / Region Switcher Tabs */}
        <div className="px-6 pt-3 border-b border-slate-200 dark:border-slate-800 flex gap-2 bg-slate-50/30 dark:bg-slate-950/30">
          <button
            onClick={() => { setActiveTab("india"); setInvoiceCurrency("INR"); }}
            className={`pb-3 px-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-all ${
              activeTab === "india"
                ? "border-emerald-500 text-emerald-600 dark:text-emerald-400"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
            }`}
          >
            <span className="text-base leading-none">🇮🇳</span>
            Indian Clients (Bank &amp; UPI)
          </button>

          <button
            onClick={() => { setActiveTab("international"); setInvoiceCurrency("USD"); }}
            className={`pb-3 px-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-all ${
              activeTab === "international"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
            }`}
          >
            <Globe className="w-4 h-4 text-blue-500" />
            International &amp; USA (PayPal / SWIFT)
          </button>

          <button
            onClick={() => setActiveTab("quickInvoice")}
            className={`pb-3 px-3 text-xs font-bold border-b-2 flex items-center gap-1.5 transition-all ${
              activeTab === "quickInvoice"
                ? "border-amber-500 text-amber-600 dark:text-amber-400"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            1-Click Payment Dispatch
          </button>
        </div>

        {/* Tab Contents */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          
          {/* ============================================================ */}
          {/* TAB 1: INDIAN CLIENTS (HDFC BANK & UPI)                      */}
          {/* ============================================================ */}
          {activeTab === "india" && (
            <div className="space-y-6 animate-in fade-in duration-150">
              
              {/* Visual Bank Card */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 p-6 text-white shadow-xl border border-blue-900/50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <span className="font-bold tracking-wide text-sm text-blue-200">
                      HDFC BANK CURRENT ACCOUNT
                    </span>
                  </div>
                  <span className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30">
                    Primary Settlement Account
                  </span>
                </div>

                <div className="my-3">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
                    Account Number
                  </span>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xl sm:text-2xl font-mono font-black tracking-widest text-emerald-400">
                      50100455367410
                    </span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.accountNumber, setCopiedAccount)}
                      className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                      title="Copy Account Number"
                    >
                      {copiedAccount ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 text-[10px] block">IFSC Code</span>
                    <span className="font-mono font-bold text-white tracking-wider">HDFC0004895</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Beneficiary</span>
                    <span className="font-bold text-white truncate block">Johnson Softwares</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] block">Account Type</span>
                    <span className="font-bold text-white">Current Account</span>
                  </div>
                </div>
              </div>

              {/* UPI & QR Code Box */}
              <div className="p-5 rounded-3xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 flex flex-col sm:flex-row items-center gap-5">
                <div className="w-36 h-36 bg-white p-2 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm shrink-0 flex items-center justify-center">
                  <img 
                    src={qrCodeUrl} 
                    alt="UPI QR Code - Johnson Softwares" 
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                    <QrCode className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      Instant UPI QR Settlement
                    </span>
                  </div>
                  
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Clients can scan this QR code with <strong>Google Pay, PhonePe, Paytm, BHIM, or any Banking App</strong> for zero-fee instant deposit.
                  </p>

                  <div className="inline-flex items-center gap-2 p-2 px-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-inner">
                    <span className="text-xs font-mono font-bold text-primary">
                      {bankDetails.upiId}
                    </span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.upiId, setCopiedUpi)}
                      className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors flex items-center gap-1"
                    >
                      {copiedUpi ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                      {copiedUpi ? "Copied" : "Copy UPI"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2.5">
                <Button
                  onClick={() => copyToClipboard(generateFormattedMessage(), setCopiedFullMessage)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold h-10 shadow-md shadow-emerald-600/20"
                >
                  {copiedFullMessage ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                  {copiedFullMessage ? "Copied Payment Template!" : "Copy Full WhatsApp Payment Message"}
                </Button>
              </div>

            </div>
          )}

          {/* ============================================================ */}
          {/* TAB 2: INTERNATIONAL & USA (PAYPAL & WIRE)                   */}
          {/* ============================================================ */}
          {activeTab === "international" && (
            <div className="space-y-6 animate-in fade-in duration-150">
              
              {/* Visual PayPal Card */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-blue-950 to-indigo-950 p-6 text-white shadow-xl border border-blue-800/50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-black italic tracking-tighter text-[#009cde]">
                      Pay<span className="text-[#00457c] dark:text-[#0079C1]">Pal</span>
                    </span>
                    <span className="font-bold text-xs text-blue-200 uppercase tracking-wider ml-1">
                      Business Merchant
                    </span>
                  </div>
                  <span className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-blue-500/20 text-cyan-300 border border-blue-400/30">
                    USD / EUR / GBP / CAD
                  </span>
                </div>

                <div className="my-3">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
                    Verified PayPal.Me Handle &amp; Link
                  </span>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xl font-mono font-black text-cyan-300">
                      paypal.me/{internationalDetails.paypalHandle}
                    </span>
                    <button
                      onClick={() => copyToClipboard(internationalDetails.paypalLink, setCopiedPaypal)}
                      className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                      title="Copy PayPal Link"
                    >
                      {copiedPaypal ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 text-[10px] block">Supported Payment Rails</span>
                    <span className="font-semibold text-slate-200">Credit Card, Debit Card, Apple Pay, PayPal</span>
                  </div>
                  <a
                    href={internationalDetails.paypalLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold text-xs shadow-md transition-colors"
                  >
                    Open PayPal Checkout <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* International Wire Transfer (SWIFT) */}
              <div className="p-5 rounded-3xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                    Direct International Inward Wire (SWIFT Transfer)
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                    <span className="text-slate-400 text-[10px] block font-semibold">Bank Name</span>
                    <span className="font-bold text-slate-900 dark:text-white">{internationalDetails.bankName}</span>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                    <span className="text-slate-400 text-[10px] block font-semibold">SWIFT Code</span>
                    <span className="font-mono font-bold text-blue-600 dark:text-blue-400">{internationalDetails.swiftCode}</span>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                    <span className="text-slate-400 text-[10px] block font-semibold">Account Number</span>
                    <span className="font-mono font-bold text-slate-900 dark:text-white">{internationalDetails.accountNumber}</span>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                    <span className="text-slate-400 text-[10px] block font-semibold">Country</span>
                    <span className="font-bold text-slate-900 dark:text-white">India</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2.5">
                <Button
                  onClick={() => copyToClipboard(generateFormattedMessage(), setCopiedFullMessage)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold h-10 shadow-md shadow-blue-600/20"
                >
                  {copiedFullMessage ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                  {copiedFullMessage ? "Copied International Template!" : "Copy International Wire & PayPal Message"}
                </Button>
              </div>

            </div>
          )}

          {/* ============================================================ */}
          {/* TAB 3: 1-CLICK PAYMENT DISPATCH GENERATOR                    */}
          {/* ============================================================ */}
          {activeTab === "quickInvoice" && (
            <div className="space-y-4 animate-in fade-in duration-150 text-xs">
              <div className="p-4 rounded-2xl bg-amber-50/50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200">
                <p className="font-semibold text-xs">
                  Generate a tailored payment request for any client in seconds and dispatch directly via WhatsApp or SMS.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-600 dark:text-slate-400 font-bold mb-1">
                    Client / Company Name
                  </label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 font-medium text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 font-bold mb-1">
                    Client Phone Number
                  </label>
                  <input
                    type="text"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 font-medium text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 font-bold mb-1">
                    Invoice Purpose / Milestone
                  </label>
                  <select
                    value={invoicePurpose}
                    onChange={(e) => {
                      const purpose = e.target.value;
                      setInvoicePurpose(purpose);
                      if (purpose.includes("Tier 1") && purpose.includes("Milestone 2")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 1210 : 61000);
                      } else if (purpose.includes("Tier 1") && purpose.includes("50%")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 325 : 17500);
                      } else if (purpose.includes("Tier 2") && purpose.includes("Milestone 2")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 2410 : 106000);
                      } else if (purpose.includes("Tier 2") && purpose.includes("50%")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 625 : 32500);
                      } else if (purpose.includes("Tier 2") && purpose.includes("3-Month Lock-in")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 1785 : 73500);
                      } else if (purpose.includes("Tier 3") && purpose.includes("Milestone 2")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 5225 : 237500);
                      } else if (purpose.includes("Tier 3") && purpose.includes("50%")) {
                        setInvoiceAmount(invoiceCurrency === "USD" ? 1475 : 72500);
                      }
                    }}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 font-medium text-slate-900 dark:text-white text-xs"
                  >
                    <option value="Tier 2: 50% Kickoff Setup Deposit">Tier 2: 50% Kickoff Setup Deposit (Flagship) [Non-Refundable]</option>
                    <option value="Tier 2: Milestone 2 Final Balance & 3-Mo Retainer">Tier 2: Milestone 2 Final Balance & 3-Mo Retainer Activation</option>
                    <option value="Tier 2: 3-Month Lock-in Managed Retainer">Tier 2: 3-Month Lock-in Managed Retainer (Quarterly)</option>
                    <option value="Tier 1: 50% Kickoff Setup Deposit">Tier 1: 50% Kickoff Setup Deposit [Non-Refundable]</option>
                    <option value="Tier 1: Milestone 2 Final Balance & 3-Mo Retainer">Tier 1: Milestone 2 Final Balance & 3-Mo Retainer Activation</option>
                    <option value="Tier 3: 50% Enterprise Setup Deposit">Tier 3: 50% Enterprise Setup Deposit [Non-Refundable]</option>
                    <option value="Tier 3: Milestone 2 Final Balance & 3-Mo Retainer">Tier 3: Milestone 2 Final Balance & 3-Mo Retainer Activation</option>
                    <option value="Custom Milestone / Feature Scope">Custom Milestone / Feature Scope</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 font-bold mb-1">
                    Amount &amp; Currency
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={invoiceCurrency}
                      onChange={(e) => {
                        const newCurr = e.target.value as "INR" | "USD";
                        setInvoiceCurrency(newCurr);
                        if (newCurr === "USD") {
                          setInvoiceAmount(invoicePurpose.includes("Milestone 2") ? 2410 : invoicePurpose.includes("3-Month") ? 1785 : 625);
                        } else {
                          setInvoiceAmount(invoicePurpose.includes("Milestone 2") ? 106000 : invoicePurpose.includes("3-Month") ? 73500 : 32500);
                        }
                      }}
                      className="px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 font-bold text-slate-900 dark:text-white"
                    >
                      <option value="INR">₹ INR</option>
                      <option value="USD">$ USD</option>
                    </select>
                    <input
                      type="number"
                      value={invoiceAmount}
                      onChange={(e) => setInvoiceAmount(Number(e.target.value))}
                      className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 font-bold text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Quick Plan Presets */}
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="text-[10px] text-slate-400 font-semibold mr-1">Quick Presets:</span>
                <button
                  type="button"
                  onClick={() => {
                    setInvoicePurpose("Tier 1: 50% Kickoff Setup Deposit");
                    setInvoiceAmount(invoiceCurrency === "USD" ? 325 : 17500);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-[10px] font-semibold text-slate-700 dark:text-slate-300 hover:bg-primary/10 hover:text-primary transition-all border border-slate-200 dark:border-slate-700"
                >
                  T1 50% Setup: {invoiceCurrency === "USD" ? "$325" : "₹17,500"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setInvoicePurpose("Tier 2: 50% Kickoff Setup Deposit");
                    setInvoiceAmount(invoiceCurrency === "USD" ? 625 : 32500);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-[10px] font-bold text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800 hover:bg-emerald-100 transition-all"
                >
                  T2 Flagship 50%: {invoiceCurrency === "USD" ? "$625" : "₹32,500"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setInvoicePurpose("Tier 2: Milestone 2 Final Balance & 3-Mo Retainer");
                    setInvoiceAmount(invoiceCurrency === "USD" ? 2410 : 106000);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-[10px] font-bold text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800 hover:bg-amber-100 transition-all"
                >
                  T2 Handover + 3-Mo Retainer: {invoiceCurrency === "USD" ? "$2,410" : "₹1,06,000"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setInvoicePurpose("Tier 2: 3-Month Lock-in Managed Retainer");
                    setInvoiceAmount(invoiceCurrency === "USD" ? 1785 : 73500);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-[10px] font-bold text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-800 hover:bg-blue-100 transition-all"
                >
                  T2 3-Mo Retainer: {invoiceCurrency === "USD" ? "$1,785" : "₹73,500"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setInvoicePurpose("Tier 3: 50% Enterprise Setup Deposit");
                    setInvoiceAmount(invoiceCurrency === "USD" ? 1475 : 72500);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-[10px] font-semibold text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-950/50 transition-all border border-slate-200 dark:border-slate-700"
                >
                  T3 50% Setup: {invoiceCurrency === "USD" ? "$1,475" : "₹72,500"}
                </button>
              </div>

              {/* Preview Box */}
              <div className="mt-4">
                <label className="block text-slate-400 text-[11px] font-bold uppercase mb-1">
                  Message Preview (Ready for WhatsApp / SMS)
                </label>
                <div className="p-3.5 rounded-2xl bg-slate-100 dark:bg-slate-950 font-mono text-[11px] leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-800">
                  {generateFormattedMessage()}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  onClick={() => copyToClipboard(generateFormattedMessage(), setCopiedFullMessage)}
                  className="flex-1 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-xs h-10"
                >
                  {copiedFullMessage ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                  {copiedFullMessage ? "Copied to Clipboard!" : "Copy Payment Message"}
                </Button>
                <Button
                  onClick={() => {
                    const cleanPhone = clientPhone.replace(/[^0-9]/g, "");
                    const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(generateFormattedMessage())}`;
                    window.open(waUrl, "_blank");
                  }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 shadow-md shadow-emerald-600/20"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  Dispatch via WhatsApp
                </Button>
              </div>

            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span>IT Act Compliant Corporate Current Account</span>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs h-8">
            Close
          </Button>
        </div>

      </div>
    </div>
  );
}
